import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form941And944 = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 941 & 944 Tax Deposit Requirements | TaxZerone"
          description="Learn about employment tax deposit requirements for Forms 941 and 944. Discover deposit schedules, penalties, and payment methods to meet IRS requirements."
          keywords=""
          canonical="resources/employment-tax/form-941-944-deposit-requirements/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Forms 941 and 944</span>
          </span>{' '}
          <br />
          Deposit Requirements for Employers
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/employment-tax/form-941-944-deposit-requirements/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-06-15T09:00:00-05:00"
                      >
                        June 15, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-06-15T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        June 15, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Forms 941 & 944 Deposit Requirements at a Glance
                  </Typography>
                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        {'Employers filing '}
                        <b>Form 941 or Form 944</b>
                        {
                          ' must deposit federal income tax, Social Security, and Medicare taxes according to IRS deposit rules. The required deposit schedule generally depends on the employer’s tax liability during the applicable lookback period.'
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <b>Tax liability under $2,500:</b>
                        {' Eligible '}
                        <Link
                          href="https://www.taxzerone.com/resources/employment-tax/form-941-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          Form 941
                        </Link>
                        {' or '}
                        <Link
                          href="https://www.taxzerone.com/resources/employment-tax/form-944-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          Form 944
                        </Link>
                        {
                          ' filers may generally pay the tax with a timely filed return instead of making deposits. '
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <b>Tax liability of $2,500 or more:</b>
                        {' Taxes must generally be deposited according to the '}
                        <b>monthly or semiweekly deposit schedule</b>
                        {'. '}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <b>Monthly Depositors:</b>
                        {' Employers with '}
                        <b>$50,000 or less</b>
                        {
                          ' in taxes during the lookback period generally deposit taxes by the '
                        }
                        <b>15th day of the following month</b>
                        {'. '}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <b>Semiweekly Depositors:</b>
                        {' Employers with '}
                        <b>more than $50,000</b>
                        {
                          ' in taxes during the lookback period deposit based on the day wages are paid. '
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="6" />
                      <span itemProp="name">
                        <b>$100,000 Next-Day Rule:</b>
                        {' If tax liability reaches '}
                        <b>$100,000 or more on any day</b>
                        {
                          ', the deposit is generally due by the next business day, and the employer becomes a semiweekly depositor. '
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="7" />
                      <span itemProp="name">
                        <b>New Employers:</b>
                        {
                          ' New employers generally begin with a monthly deposit schedule unless the $100,000 next-day deposit rule applies. '
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="8" />
                      <span itemProp="name">
                        <b>Electronic Deposits:</b>
                        {' Employment tax deposits must '}
                        generally be
                        {' made electronically through methods such as '}
                        <Link
                          href="https://www.taxzerone.com/resources/eftps/"
                          sx={{ color: '#0202ff' }}
                        >
                          <b>EFTPS</b>
                        </Link>
                        <b>, IRS Business Tax Account, or IRS Direct Pay</b>
                        {'. '}
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Employers must deposit employment taxes reported on{' '}
                  <Link
                    href="/resources/employment-tax/form-941-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 941
                  </Link>{' '}
                  or{' '}
                  <Link
                    href="/resources/employment-tax/form-944-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 944
                  </Link>
                  , including federal income tax and Social Security and
                  Medicare contributions. Your prior filings determine whether
                  you follow a monthly or semiweekly deposit schedule. These
                  schedules are based on wage payment dates, not accounting
                  accruals. For details on deposit rules and filing
                  requirements, see IRS{' '}
                  <Link
                    href="https://www.irs.gov/taxtopics/tc758"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    Topic No. 758
                  </Link>
                  .
                </Typography>

                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What are Forms 941 and 944?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#tax" sx={{ color: '#0202ff' }}>
                          Tax Liabilities under $2,500
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#liability" sx={{ color: '#0202ff' }}>
                          Tax Liabilities of $2,500 or More
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#emp" sx={{ color: '#0202ff' }}>
                          Employment Tax Deposit Schedules
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#month" sx={{ color: '#0202ff' }}>
                          Monthly Schedule Depositor
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#semi" sx={{ color: '#0202ff' }}>
                          Semiweekly Schedule Depositor
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#rule" sx={{ color: '#0202ff' }}>
                          $100,000 Next-Day Deposit Rule
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#new" sx={{ color: '#0202ff' }}>
                          New Employer Guidelines
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#how" sx={{ color: '#0202ff' }}>
                          How to Make Payroll Tax Deposits
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#pay" sx={{ color: '#0202ff' }}>
                          Alternative Payment Methods
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#same" sx={{ color: '#0202ff' }}>
                          Same-Day Payments
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#penalty" sx={{ color: '#0202ff' }}>
                          Penalties for Late or Incomplete Deposits
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="what"
                >
                  What are Forms 941 and 944?
                </Typography>

                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b> Form 941:</b> This form is filed quarterly and is used
                    to report federal income tax withheld from employees, as
                    well as Social Security and Medicare taxes.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b> Form 944:</b> Smaller employers who owe $1,000 or less
                    in payroll taxes annually may be eligible to file this form.
                    Unlike Form 941, it is filed once a year.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The IRS determines whether you should file Form 941 or Form
                  944 based on your payroll size. Typically, the IRS notifies
                  eligible small businesses when they can use Form 944.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="tax"
                >
                  Tax Liabilities under $2,500
                </Typography>

                <Box component="ul" sx={{ mb: 3.5, '& li, ul': { mb: 2 } }}>
                  <li>
                    <b>Form 941 Filers: </b>
                  </li>
                  <ul>
                    <li>
                      If you are required to{' '}
                      <Link
                        href="/employment-tax/e-file-irs-form-941/"
                        sx={{ color: '#0202ff' }}
                      >
                        file Form 941
                      </Link>{' '}
                      and your employment tax liability for the preceding or
                      current quarter is less than $2,500, you may pay the taxes
                      directly with your timely filed return instead of making a
                      deposit.
                    </li>
                    <li>
                      However, this option is only available if you do not incur
                      a $100,000 next-day deposit obligation during the current
                      quarter.
                    </li>
                  </ul>
                  <li>
                    <b>Form 944 Filers:</b>
                  </li>
                  <ul>
                    <li>
                      If you are required to{' '}
                      <Link
                        href="/employment-tax/e-file-irs-form-944/"
                        sx={{ color: '#0202ff' }}
                      >
                        file Form 944
                      </Link>{' '}
                      and your employment tax liability for the year is less
                      than $2,500, you can pay the amount with your timely filed
                      return instead of making deposits.
                    </li>
                    <li>
                      For a fourth-quarter liability of less than $2,500, you
                      may pay it with your return if deposits were made for the
                      first three quarters.
                    </li>
                  </ul>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="liability"
                >
                  Tax Liabilities of $2,500 or More
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  For liabilities of $2,500 or more:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1 } }}>
                  <li>
                    You must deposit taxes according to the <b>monthly</b> or{' '}
                    <b>semiweekly</b> rules.
                  </li>
                  <li>
                    If you&apos;re unsure whether your total liability will be
                    less than $2,500 for the current quarter, follow the
                    appropriate deposit schedule to avoid penalties.
                  </li>
                </Box>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        E-file 94x Employment Tax Forms with TaxZerone
                      </Typography>
                      <Typography variant="body2">
                        Simplify your payroll tax filings for Form 940, 941,
                        943, 944, and 945. Report wages, taxes, and
                        contributions with ease. Avoid penalties—file on time!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941-944deposit`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="emp"
                >
                  Employment Tax Deposit Schedules
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  When it comes to depositing your employment taxes, you&apos;ll
                  need to follow one of two deposit schedules: <b>monthly</b> or{' '}
                  <b>semiweekly</b>. The schedule that applies to you depends on
                  the amount of taxes you reported during your{' '}
                  <b>lookback period</b>. see IRS{' '}
                  <Link
                    href="https://www.irs.gov/taxtopics/tc757"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    Topic No. 757
                  </Link>
                  .
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  What is the Lookback Period?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Your lookback period is determined by the amount of employment
                  taxes you reported. Here’s how it works:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1 } }}>
                  <li>
                    If you&apos;ve only filed{' '}
                    <Link
                      href="/employment-tax/e-file-irs-form-941/"
                      sx={{ color: '#0202ff' }}
                    >
                      IRS Form 941
                    </Link>
                    , your lookback period covers the 12 months (4 quarters)
                    starting on <b>July 1st of the second preceding year</b> and
                    ending on <b>June 30th of the prior year</b>.
                  </li>
                  <li>
                    If you’ve filed{' '}
                    <Link
                      href="/employment-tax/e-file-irs-form-944/"
                      sx={{ color: '#0202ff' }}
                    >
                      IRS Form 944
                    </Link>{' '}
                    in the past or are filing it this year, your lookback period
                    is the <b>calendar year two years before</b> the current
                    year.
                  </li>
                </Box>
                <Typography variant="body2" sx={{ mb: 3 }}>
                  For more information on the lookback period and depositing
                  taxes, check{' '}
                  <Link
                    href="https://www.irs.gov/forms-pubs/about-publication-15"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    Publication 15
                  </Link>
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="month"
                >
                  Monthly Schedule Depositor
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you reported <b>$50,000</b> or less in employment taxes
                  during your lookback period, you&apos;re considered a{' '}
                  <b>monthly schedule depositor</b>. This means you&apos;ll
                  typically need to deposit taxes for payments made during a
                  month by <b>the 15th day of the following month</b>. For
                  instance, taxes on payments made in January must be deposited
                  by <b>February 15th</b>.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Important Note:</b> If the 15th falls on a Saturday,
                  Sunday, or legal holiday in the District of Columbia, your
                  deposit is due by the next business day.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="semi"
                >
                  Semiweekly Schedule Depositor
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your reported taxes exceed $50,000 during the lookback
                  period, you&apos;re a semiweekly schedule depositor. You must
                  deposit taxes according to the following schedule:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    For wages paid on Wednesday, Thursday, and/or Friday,
                    deposits are due the following Wednesday.
                  </li>
                  <li>
                    For wages paid on other days, deposits are due by the
                    following Friday.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  It’s essential to note that these schedules are based on your
                  tax liability, not your payroll frequency.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="rule"
                >
                  $100,000 Next-Day Deposit Rule
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Whether you are on the monthly or semiweekly schedule, if your
                  accumulated taxes total <b>$100,000 or more</b> on any single
                  day, you must deposit them by the <b>next business day</b>.
                  This rule requires you to switch to the{' '}
                  <b>semiweekly depositor schedule</b> for the rest of the
                  calendar year and the following year.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="new"
                >
                  New Employer Guidelines
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b> Starting a business?</b> If you&apos;re a new employer,
                  your tax liability for any quarter before you started or
                  acquired your business is considered zero. As a result, in
                  your first year, you&apos;ll be on a monthly deposit schedule
                  unless the $100,000 next-day deposit rule applies.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="how"
                >
                  How to Make Payroll Tax Deposits
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  To stay compliant with federal tax requirements, all tax
                  deposits must be made electronically. You can make payments
                  through the IRS{' '}
                  <Link
                    href="https://www.irs.gov/businesses/business-tax-account"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    Business Tax Account
                  </Link>
                  ,{' '}
                  <Link
                    href="https://www.irs.gov/payments/pay-business-taxes-from-your-bank-account"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    IRS Direct Pay for businesses
                  </Link>
                  , or by using{' '}
                  <Link
                    href="https://www.irs.gov/payments/eftps-the-electronic-federal-tax-payment-system"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    EFTPS (Electronic Federal Tax Payment System)
                  </Link>
                  , a free service offered by the U.S. Department of Treasury.
                </Typography>
                <Typography variant="body2" sx={{ mb: 3 }}>
                  Learn more about the IRS{' '}
                  <Link href="/resources/eftps/" sx={{ color: '#0202ff' }}>
                    EFTPS tax payment method
                  </Link>
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Key Details for EFTPS
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <b>Deadline:</b> To ensure your deposit is on time, schedule
                    it by 8 p.m. Eastern Time the day before it&apos;s due.
                  </li>
                  <li>
                    <b>Enrollment:</b> Visit{' '}
                    <Link
                      href="https://www.eftps.gov"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      EFTPS.gov
                    </Link>{' '}
                    or call 800-555-4477 (800-733-4829 for TDD) to sign up.
                  </li>
                  <li>
                    <b>Additional Resources:</b> You can refer to{' '}
                    <Link
                      href="https://www.irs.gov/pub/irs-pdf/p966.pdf"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      Publication 966
                    </Link>
                    , Electronic Federal Tax Payment System: A Guide to Getting
                    Started, for more information.
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="pay"
                >
                  Alternative Payment Methods
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you prefer not to use EFTPS directly, you can arrange for a
                  trusted third party—such as a tax professional, financial
                  institution, or payroll service—to deposit on your behalf.
                  Keep in mind, that these third parties may charge a fee and
                  have different cutoff times.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="same"
                >
                  Same-Day Payments
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you miss the 8 p.m. Eastern deadline for an EFTPS deposit
                  transaction, you can still make your deposit on time using the{' '}
                  <Link
                    href="https://www.fiscal.treasury.gov/federal-tax-collection/"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    Federal Tax Collection Service (FTCS)
                  </Link>{' '}
                  for same-day payments. Be sure to arrange this option in
                  advance with your financial institution, as they may charge a
                  fee, and service availability may vary.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="penalty"
                >
                  Penalties for Late or Incomplete Deposits
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  You are required to deposit the full amount (100%) of your tax
                  liability by the due date. If you make a late deposit, pay
                  less than required, or mail payments directly to the IRS
                  instead of making the proper deposit, penalties may apply.
                  However, in certain cases, the IRS may choose to waive these
                  penalties.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Start filing your Employment Taxes today!
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              E-file your employment tax forms with TaxZerone for{' '}
              <b>just $6.99 per return</b>.{' '}
              <Hidden smDown>
                <br />
              </Hidden>
              Employers trust TaxZerone to simplify their filing process.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941-944deposit`}
              >
                E-file Now!
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form941And944;
